/**
 * 来場者スタンプ帳ユーティリティ
 * スタンプ帳の集計結果と新規押印島を算出します。
 */

import { STAMP_ISLANDS } from '../../../shared/constants/stampIslands';

/** 島スタンプ閾値の最小入力 */
type IslandThresholdLike = {
  islandId?: string;
  requiredCount?: number;
};

/**
 * 島別進捗の既定値を返す
 * @returns {{ total: number, stamped: number, isCompleted: boolean }} 既定進捗
 */
const createEmptyStampSummary = () => ({
  total: 0,
  stamped: 0,
  requiredCount: 0,
  isCompleted: false,
});

/**
 * 島の公開企画数に収まる有効閾値を返す
 * @param {string} islandId - 島ID
 * @param {number} publishedEventCount - 公開企画数
 * @param {Array<{ islandId?: string, requiredCount?: number }>} islandThresholds - 島設定
 * @returns {number} 有効閾値
 */
const resolveEffectiveIslandThreshold = (
  islandId,
  publishedEventCount,
  islandThresholds: readonly IslandThresholdLike[],
) => {
  if (publishedEventCount <= 0) {
    return 0;
  }

  const configuredThreshold = (Array.isArray(islandThresholds)
    ? islandThresholds
    : []
  ).find((threshold) => threshold?.islandId === islandId);
  const numericRequiredCount = Number(configuredThreshold?.requiredCount);
  const requiredCount = Number.isInteger(numericRequiredCount)
    ? numericRequiredCount
    : 1;

  return Math.min(Math.max(requiredCount, 1), publishedEventCount);
};

/**
 * 日時文字列をタイムスタンプへ変換する
 * @param {string} value - ISO日時文字列
 * @returns {number | null} タイムスタンプ
 */
const normalizeTimestamp = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsedTimestamp = Date.parse(value);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

/**
 * 訪問履歴と一時保存中の押印演出データを合成する
 * サーバー反映前でも島スタンプの見た目を即時反映するために使う
 * @param {Array<{ eventId?: string, claimedAt?: string }>} eventVisits - 訪問履歴
 * @param {Array<{ eventId?: string, claimedAt?: string }>} pendingCelebrations - 一時保存中の押印演出
 * @returns {Array<{ eventId: string, claimedAt: string }>} 合成後の訪問履歴
 */
export const mergeEventVisitsWithPendingCelebrations = (eventVisits, pendingCelebrations) => {
  /** 正規化済み訪問履歴 */
  const normalizedVisits = Array.isArray(eventVisits) ? eventVisits : [];

  /** 正規化済み一時保存押印演出 */
  const normalizedPendingCelebrations = Array.isArray(pendingCelebrations) ? pendingCelebrations : [];

  /** eventId ごとの訪問履歴 */
  const visitsByEventId = new Map();

  normalizedVisits.forEach((visit) => {
    if (typeof visit?.eventId !== 'string' || visit.eventId.trim() === '') {
      return;
    }

    visitsByEventId.set(visit.eventId, {
      eventId: visit.eventId,
      claimedAt: typeof visit?.claimedAt === 'string' ? visit.claimedAt : '',
    });
  });

  normalizedPendingCelebrations.forEach((celebration) => {
    if (typeof celebration?.eventId !== 'string' || celebration.eventId.trim() === '') {
      return;
    }

    /** 既存訪問履歴 */
    const currentVisit = visitsByEventId.get(celebration.eventId) ?? null;

    /** 既存タイムスタンプ */
    const currentTimestamp = normalizeTimestamp(currentVisit?.claimedAt);

    /** 一時保存タイムスタンプ */
    const pendingTimestamp = normalizeTimestamp(celebration?.claimedAt);

    /** 一時保存データで上書きすべきか */
    const shouldReplaceCurrentVisit = !currentVisit
      || currentTimestamp === null
      || (pendingTimestamp !== null && pendingTimestamp >= currentTimestamp);

    if (!shouldReplaceCurrentVisit) {
      return;
    }

    visitsByEventId.set(celebration.eventId, {
      eventId: celebration.eventId,
      claimedAt: typeof celebration?.claimedAt === 'string' ? celebration.claimedAt : '',
    });
  });

  return Array.from(visitsByEventId.values());
};

/**
 * 公開イベントと達成済みイベントから島別進捗を作る
 * @param {Array<{ eventId?: string, location?: string, category?: string }>} publishedEvents - 公開イベント
 * @param {Array<{ eventId?: string }>} eventVisits - 達成済みイベント
 * @param {Array<{ islandId?: string, requiredCount?: number }>} [islandThresholds] - 島別押印閾値
 * @returns {Record<string, { total: number, stamped: number, requiredCount: number, isCompleted: boolean }>} 島別進捗
 */
export const buildStampSummaryByIslandId = (
  publishedEvents,
  eventVisits,
  islandThresholds: readonly IslandThresholdLike[] = [],
) => {
  /** 正規化済みイベント一覧 */
  const normalizedEvents = Array.isArray(publishedEvents) ? publishedEvents : [];

  /** 正規化済み達成一覧 */
  const normalizedVisits = Array.isArray(eventVisits) ? eventVisits : [];

  /** 達成済みイベントID集合 */
  const visitedEventIds = new Set(
    normalizedVisits
      .map((visit) => (typeof visit?.eventId === 'string' ? visit.eventId : ''))
      .filter(Boolean),
  );

  return STAMP_ISLANDS.reduce((summaryByIslandId, island) => {
    /** 島に属するイベント */
    const islandEventIds = new Set(
      normalizedEvents
        .filter((event) => {
          return event?.location === island.locationValue
            || event?.category === island.locationValue;
        })
        .map((event) => (
          typeof event?.eventId === 'string' ? event.eventId : ''
        ))
        .filter(Boolean),
    );

    /** 公開企画数 */
    const total = islandEventIds.size;

    /** 押印済み公開企画数 */
    const stamped = Array.from(islandEventIds).filter(
      (eventId) => visitedEventIds.has(eventId),
    ).length;

    /** 島スタンプの押印必要企画数 */
    const requiredCount = resolveEffectiveIslandThreshold(
      island.id,
      total,
      islandThresholds,
    );

    summaryByIslandId[island.id] = {
      total,
      stamped,
      requiredCount,
      isCompleted: total > 0 && stamped >= requiredCount,
    };

    return summaryByIslandId;
  }, {});
};

/**
 * 前回未押印で今回押印済みになった島IDを返す
 * @param {Record<string, { isCompleted?: boolean }>} previousSummaryByIslandId - 前回進捗
 * @param {Record<string, { isCompleted?: boolean }>} nextSummaryByIslandId - 今回進捗
 * @returns {string[]} 新規押印島ID一覧
 */
export const resolveNewlyStampedIslandIds = (previousSummaryByIslandId, nextSummaryByIslandId) => {
  /** 正規化済み前回進捗 */
  const normalizedPreviousSummaryByIslandId = previousSummaryByIslandId ?? {};

  /** 正規化済み今回進捗 */
  const normalizedNextSummaryByIslandId = nextSummaryByIslandId ?? {};

  return STAMP_ISLANDS.filter((island) => {
    /** 前回押印済みか */
    const wasCompleted = Boolean(normalizedPreviousSummaryByIslandId[island.id]?.isCompleted);

    /** 今回押印済みか */
    const isCompleted = Boolean(normalizedNextSummaryByIslandId[island.id]?.isCompleted);

    return !wasCompleted && isCompleted;
  }).map((island) => island.id);
};

/**
 * イベントID一覧から該当する島ID一覧を返す
 * @param {Array<{ eventId?: string, location?: string, category?: string }>} publishedEvents - 公開イベント
 * @param {string[]} eventIds - イベントID一覧
 * @returns {string[]} 島ID一覧
 */
export const resolveIslandIdsByEventIds = (publishedEvents, eventIds) => {
  /** 正規化済みイベント一覧 */
  const normalizedEvents = Array.isArray(publishedEvents) ? publishedEvents : [];

  /** 正規化済みイベントID集合 */
  const normalizedEventIdSet = new Set(
    (Array.isArray(eventIds) ? eventIds : [])
      .map((eventId) => (typeof eventId === 'string' ? eventId : ''))
      .filter(Boolean),
  );

  return STAMP_ISLANDS.filter((island) => {
    return normalizedEvents.some((event) => {
      const belongsToIsland = event?.location === island.locationValue || event?.category === island.locationValue;
      return belongsToIsland && normalizedEventIdSet.has(event?.eventId);
    });
  }).map((island) => island.id);
};

/**
 * 一時保存された押印演出データから、本当に新規押印だった島IDだけを返す
 * @param {Array<{ eventId?: string, location?: string, category?: string }>} publishedEvents - 公開イベント
 * @param {Array<{ eventId?: string, claimedAt?: string }>} eventVisits - 達成済みイベント
 * @param {Array<{ eventId?: string, claimedAt?: string }>} pendingCelebrations - 一時保存された押印演出
 * @param {Array<{ islandId?: string, requiredCount?: number }>} [islandThresholds] - 島別押印閾値
 * @returns {string[]} 新規押印島ID一覧
 */
export const resolvePendingStampCelebrationIslandIds = (
  publishedEvents,
  eventVisits,
  pendingCelebrations,
  islandThresholds: readonly IslandThresholdLike[] = [],
) => {
  /** 正規化済みイベント一覧 */
  const normalizedEvents = Array.isArray(publishedEvents) ? publishedEvents : [];

  /** 正規化済み達成一覧 */
  const normalizedVisits = Array.isArray(eventVisits) ? eventVisits : [];

  /** 正規化済み一時保存演出一覧 */
  const normalizedPendingCelebrations = Array.isArray(pendingCelebrations) ? pendingCelebrations : [];

  /** 達成済み・一時保存中イベントID集合 */
  const currentVisitedEventIdSet = new Set(
    [...normalizedVisits, ...normalizedPendingCelebrations]
      .map((visit) => (typeof visit?.eventId === 'string' ? visit.eventId : ''))
      .filter(Boolean),
  );

  return STAMP_ISLANDS.filter((island) => {
    /** 島に属するイベント一覧 */
    const islandEvents = normalizedEvents.filter((event) => {
      return event?.location === island.locationValue || event?.category === island.locationValue;
    });

    /** 島に属するイベントID集合 */
    const islandEventIdSet = new Set(
      islandEvents
        .map((event) => (typeof event?.eventId === 'string' ? event.eventId : ''))
        .filter(Boolean),
    );

    if (islandEventIdSet.size === 0) {
      return false;
    }

    /** 島スタンプの押印必要企画数 */
    const requiredCount = resolveEffectiveIslandThreshold(
      island.id,
      islandEventIdSet.size,
      islandThresholds,
    );

    /** 現在時点の島内達成企画数 */
    const currentCompletedCount = Array.from(islandEventIdSet).filter(
      (eventId) => currentVisitedEventIdSet.has(eventId),
    ).length;

    if (requiredCount === 0 || currentCompletedCount < requiredCount) {
      return false;
    }

    /** 島に属する一時保存演出 */
    const islandCelebrations = normalizedPendingCelebrations.filter((celebration) => {
      return typeof celebration?.eventId === 'string' && islandEventIdSet.has(celebration.eventId);
    });

    if (islandCelebrations.length === 0) {
      return false;
    }

    /** 一時保存演出の最も古い押印時刻 */
    const earliestCelebrationTimestamp = islandCelebrations.reduce((currentEarliestTimestamp, celebration) => {
      const celebrationTimestamp = normalizeTimestamp(celebration?.claimedAt);

      if (celebrationTimestamp === null) {
        return currentEarliestTimestamp;
      }

      if (currentEarliestTimestamp === null || celebrationTimestamp < currentEarliestTimestamp) {
        return celebrationTimestamp;
      }

      return currentEarliestTimestamp;
    }, null);

    if (earliestCelebrationTimestamp === null) {
      return false;
    }

    /** 一時保存演出より前に達成していた島内企画ID */
    const earlierVisitedEventIds = new Set(normalizedVisits.flatMap((visit) => {
      if (!islandEventIdSet.has(visit?.eventId)) {
        return [];
      }

      const visitTimestamp = normalizeTimestamp(visit?.claimedAt);
      return visitTimestamp !== null && visitTimestamp < earliestCelebrationTimestamp
        ? [visit.eventId]
        : [];
    }));

    return earlierVisitedEventIds.size < requiredCount;
  }).map((island) => island.id);
};

/**
 * 島別進捗の空状態を作る
 * @returns {Record<string, { total: number, stamped: number, requiredCount: number, isCompleted: boolean }>} 空進捗
 */
export const createEmptyStampSummaryByIslandId = () => {
  return STAMP_ISLANDS.reduce((summaryByIslandId, island) => {
    summaryByIslandId[island.id] = createEmptyStampSummary();
    return summaryByIslandId;
  }, {});
};
